// import React, { useState, useEffect } from "react";

import React, { useState, useEffect, useRef } from "react";
import { getSupabase } from "./supabaseClient";
import {
  HStack,
  VStack,
  Button,
  Input,
  Box,
  Heading,
  Td,
  Th,
  Tr,
  Thead,
  Table,
  Tbody,
  InputGroup,
  InputRightElement,
  IconButton,
  Text,
  Spinner,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  useToast,
} from "@chakra-ui/react";
import {
  prompts,
  tableName,
  bucketName_files,
  supabaseUrl,
  getBackendURL,
  file_reference_column,
} from "./env.js";
import {
  isInGroup,
  getColumnLabels,
  getScheduleField,
  getLabel,
  hasClinicalAdminPermissions,
} from "../config/roles";
import { sharedUsersOverlapOrClauses, resolveTenantRowAccess } from "../utils/shareAccess";
import { removeVisitRows } from "../utils/removeVisitRows";
import { mapRosterRowToPatient } from "../hooks/medstudent/usePatientDocument";
import { planRosterSearch, sortSearchResults } from "../utils/patientSearchRoster";
import { applyContainsFilters, isUnreadableDate, DATE_SEARCH_COLUMNS } from "../utils/patientNameSearch";
import {
  isCutoverEffective,
  startSearchVisitsFromRds, serveSearchVisitsFromRds,
  startSearchRosterFromRds, serveSearchRosterFromRds,
} from "../utils/rdsCutover";
import { useHideFloatingBubbles } from "../utils/floatingBubbles";
import { foldersWithFiles } from "../utils/reportsFolderFiles";
import { useSelectedProviders } from "../hooks/useSelectedProviders";
import ProvidersDropdown from "./ProvidersDropdown";
import jsonToFormattedText from "./JSONToMarkdown.js";
import { useLocation, useNavigate } from "react-router-dom";
import Uploader from "./upload/Uploader.js";
import ResultsTable from "./table/ResultsTable.js";
import "../css/SearchComponent.css";
import { FiX } from "react-icons/fi";
import EditPatientModal from "./EditPatientModal";
import {
  hydrateScheduleRowsTimezoneSource,
  fetchViewerIanaTimezone,
} from "../utils/scheduleRowTimezone.js";

// The Vita ID search field / results column. Added here rather than to env's
// `prompts`: that list also drives the add/edit patient forms, folder naming
// and the CSV export, none of which should grow a Vita ID box — the column is
// assigned by trigger, never typed.
const VITA_ID_PROMPT = ["vita_id", "Vita ID", "Optional", ""];

const SearchComponent = ({ onPatientSelect, showCheckboxes = true, disableUploader = false }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // The floating Reports / Record / Chat bubbles sit over the bottom-right of
  // the results table — exactly where its Reports / JSON buttons and the
  // horizontal scrollbar end up. Same request the fax-assign modal makes, and
  // it is counted, so making it again inside that modal is harmless.
  useHideFloatingBubbles(true);

  // Cross-schema providers: /search spans every selected schema's notes-c.
  const { selected, homeSchema, allSchemas, selectAll } = useSelectedProviders();

  // Entering search ticks the WHOLE clinic group. Searching is the one place
  // you expect to look everywhere by default — arriving here with a narrowed
  // selection left over from Schedules silently hides results, which reads as
  // "the patient isn't in the system" rather than "you filtered them out".
  //
  // Keyed on the resolved group so it fires once the async schema lookup lands,
  // not on the single-schema placeholder the hook starts with. selectAll is a
  // no-op when everything is already ticked, so this doesn't loop.
  useEffect(() => {
    if (allSchemas.length > 1) selectAll();
  }, [allSchemas, selectAll]);
  const [searchValues, setSearchValues] = useState({});
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [results, setResults] = useState([]);
  const [selectedRows, setSelectedRows] = useState({});
  const [userRole, setUserRole] = useState(null);
  // Who is searching, resolved by runSearch: the same inputs the schedules
  // table feeds resolveTenantRowAccess, so "may I delete this row" answers
  // the same here as there.
  const [viewer, setViewer] = useState({ userId: null, role: null, ownerIds: [], ownerRoles: {} });
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const deleteCancelRef = useRef(null);
  const toast = useToast();
  const [isSearching, setIsSearching] = useState(false);
  // Distinguishes "you haven't searched yet" from "that search found nothing".
  // Without it an empty result set renders exactly like a fresh page, so the
  // spinner vanishing tells you nothing.
  const [hasSearched, setHasSearched] = useState(false);
  // Set when the roster couldn't answer this search — see planRosterSearch.
  // Named out loud rather than left as a silent gap in the results.
  const [rosterSkipped, setRosterSkipped] = useState([]);
  // Set when the viewer's role is what excluded the patient records. Kept
  // separate from `rosterSkipped` because it is not something clearing a field
  // will fix — and left silent it looks exactly like the patient not existing.
  const [rosterRoleBlocked, setRosterRoleBlocked] = useState(false);
  // Which rows' report folders actually hold something — see the effect below.
  // `for` is the results array the check ran against, so "checked" is derived
  // (`reportCheck.for === results`) and is false the moment new results land:
  // no frame where new rows render against the previous search's folders.
  const [reportCheck, setReportCheck] = useState({ for: null, folders: new Set() });

  const [currentFolderFiles, setCurrentFolderFiles] = useState([]);
  const [currentFolderPath, setCurrentFolderPath] = useState("");
  const [previousFolderPath, setPreviousFolderPath] = useState("");
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState({});
  const [isEditPatientModalOpen, setIsEditPatientModalOpen] = useState(false);
  const [viewerTimezone, setViewerTimezone] = useState("America/New_York");

  // `reports` on a row is a pointer, not proof of files: the roster build stamps
  // one on every patient record, and a visit keeps its pointer after the folder
  // is emptied. So every row's folder is listed before the table shows a View
  // Reports button (utils/reportsFolderFiles.js). Keyed on `results` so an
  // edit or upload that replaces a row re-checks; a check the results changed
  // underneath must not overwrite the newer one.
  useEffect(() => {
    let stale = false;
    (async () => {
      let found = new Set();
      try {
        const supabase = await getSupabase();
        found = await foldersWithFiles(
          supabase,
          bucketName_files,
          results.map((r) => r[file_reference_column]),
        );
      } catch (e) {
        console.warn("[search] report folder check failed:", e?.message);
      }
      if (!stale) setReportCheck({ for: results, folders: found });
    })();
    return () => {
      stale = true;
    };
  }, [results]);

  // Fetch user role on mount to show role-appropriate search fields
  useEffect(() => {
    (async () => {
      try {
        const supabase = await getSupabase();
        setViewerTimezone(await fetchViewerIanaTimezone(supabase));
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { data } = await supabase.from("users").select("role").eq("id", user.id).single();
        if (data?.role) setUserRole(data.role);
      } catch (_) {}
    })();
  }, []);

  // Build role-appropriate search prompts using actual DB column names. The
  // results table takes its columns from this same list, so a field added here
  // is also a column there. Vita ID sits beside the EHR ID, the other
  // identifier.
  const searchPrompts = (() => {
    const hiddenCols = getScheduleField(userRole, 'hiddenColumns');
    const labels = getColumnLabels(userRole);
    const base = prompts
      .filter(([col]) => !hiddenCols.includes(col))
      .map(([col, defaultLabel, ...rest]) => [col, labels[col] || defaultLabel, ...rest]);
    const ehrAt = base.findIndex(([col]) => col === "EHR_ID");
    const at = ehrAt >= 0 ? ehrAt + 1 : base.length;
    return [...base.slice(0, at), VITA_ID_PROMPT, ...base.slice(at)];
  })();

  // Column key → the label the search form shows for it, for the
  // roster-skipped note. `date range` is not a column, so it is spelled out.
  const rosterFieldLabel = (column) => {
    if (column === "date range") return "appointment date";
    const found = searchPrompts.find(([col]) => col === column);
    return String(found?.[1] || column).toLowerCase();
  };

  const handleClearAll = () => {
    setSearchValues({});
    setStartDate("");
    setEndDate("");
  };

  const handleSearch = async () => {
    // Every exit below — including the four early returns — has to clear this,
    // so the whole body runs inside the try/finally rather than each branch
    // remembering to reset the flag.
    setIsSearching(true);
    setHasSearched(true);
    setRosterSkipped([]);
    setRosterRoleBlocked(false);
    try {
      await runSearch();
    } catch (error) {
      console.error("Error searching data:", error);
    } finally {
      setIsSearching(false);
    }
  };

  const runSearch = async () => {
    // A date column cannot take a substring: the DOB box accepts any format
    // (9/1/2026, sept 1 2026, 2026-09-01, sep 2026, 1970 …) and is matched as
    // a date, so a value that is not a date is refused here with the reason
    // rather than sent on and swallowed as "no results".
    const unreadable = Object.entries(searchValues).find(([col, v]) => isUnreadableDate(col, v));
    if (unreadable) {
      const label = (searchPrompts.find(([col]) => col === unreadable[0]) || [])[1] || unreadable[0];
      toast({
        title: `Could not read the ${label}`,
        description: `"${unreadable[1]}" is not a date I can search. Try 9/1/2026, Sept 1 2026, 2026-09-01 — or just a month (Sep 2026) or a year (1970).`,
        status: "warning",
        duration: 7000,
        isClosable: true,
      });
      return;
    }
    const supabase = await getSupabase();
    const { data: sessionData, error: sessionError } =
      await supabase.auth.getSession();

    if (sessionError) {
      console.error("Error getting session:", sessionError);
      return;
    }

    const userId = sessionData?.session?.user?.id;
    const userEmail = sessionData?.session?.user?.email;

    if (!userId) {
      console.error("User ID not found in session.");
      return;
    }

    // Fetch user role from the users table
    const { data: userData, error: userError } = await supabase
      .from("users")
      .select("role")
      .eq("email", userEmail)
      .single();

    if (userError) {
      console.error("Error fetching user role:", userError);
      return;
    }

    const role = userData.role;

    // Access-control owner-ids resolved ONCE, reused for every schema.
    let ownerIds = [];
    let ownerRoles = {};
    if (role !== "admin" && role !== "doctor" && isInGroup(role, 'tenantSchema')) {
      try {
        const backendUrl = getBackendURL();
        const res = await fetch(`${backendUrl}/api/shared-all-owner-ids/?user_id=${encodeURIComponent(userId)}`);
        const json = await res.json().catch(() => ({}));
        if (res.ok && Array.isArray(json.owner_ids)) ownerIds = json.owner_ids;
        if (res.ok && json.owner_roles && typeof json.owner_roles === "object") ownerRoles = json.owner_roles;
      } catch (_) {}
    }
    setViewer({ userId, role, ownerIds, ownerRoles });

    // Build the SAME query against a given schema's client.
    const buildQuery = (client) => {
      let query = client.from(tableName).select("*");

      // Only apply user_id filter for regular users (not admin or doctor)
      if (role !== "admin" && role !== "doctor") {
        if (isInGroup(role, 'tenantSchema')) {
          // Tenant users: own records OR records where they're in shared_users
          // OR records from owners who shared all rows with them
          const orParts = [
            `user_id.eq.${userId}`,
            ...sharedUsersOverlapOrClauses(userId),
          ];
          if (ownerIds.length > 0) {
            orParts.push(`user_id.in.(${ownerIds.join(",")})`);
          }
          query = query.or(orParts.join(","));
        } else {
          query = query.eq("user_id", userId);
        }
      }

      // One ilike per filled field — or one per WORD for a name field, so
      // "Pamela Rogers" finds "Rogers, Pamela". See patientNameSearch.js.
      Object.keys(searchValues).forEach((columnName) => {
        query = applyContainsFilters(query, columnName, searchValues[columnName]);
      });

      // Add date range filters
      if (!startDate && !endDate) {
      } else if (!startDate || !endDate) {
        query = query.eq("date_", endDate ? endDate : startDate);
      } else {
        query = query.gte("date_", startDate).lte("date_", endDate);
      }
      return query.order("date_", { ascending: false });
    };

    // The roster answers a different question from `notes-c`: it is
    // patient-level, so it finds people who have never been scheduled and whom
    // a visit search therefore cannot see at all. It can only serve filters
    // that map onto columns it actually has — planRosterSearch decides, and
    // says why when it can't.
    const rosterPlan = planRosterSearch({ searchValues, startDate, endDate });
    // Same gate the roster page and the quick-add dropdown apply: the roster is
    // a clinical surface, and it is NOT user-scoped, so a non-clinical role
    // must not reach it.
    const includeRoster = rosterPlan.include && hasClinicalAdminPermissions(role);

    const buildRosterQuery = (client) => {
      let query = client.from("patients_with_ehrid").select("*");
      rosterPlan.filters.forEach(([column, value]) => {
        query = applyContainsFilters(query, column, value);
      });
      return query.order("patient_name", { ascending: true, nullsFirst: false });
    };

    // Cross-schema: run it against every SELECTED provider schema and merge.
    // A single selected schema (the default) reduces to the previous behavior.
    // Each row is tagged `_schema` so ResultsTable's row-click opens the chart
    // against the patient's own schema.
    const schemas = selected.length ? selected : [homeSchema];
    // Phase 4 cutover, site 8 (utils/rdsCutover.js `search_visits` /
    // `search_roster`): when both switches are on, each table's rows are
    // SERVED by RDS while the Supabase read still runs as the parity
    // oracle and the fallback. Off (the default, and whenever RDS says a
    // flag is not effective) this is byte-for-byte the old behaviour.
    const cutoverVisits = await isCutoverEffective("search_visits");
    const cutoverRoster = includeRoster && await isCutoverEffective("search_roster");
    const perSchema = await Promise.all(
      schemas.map(async (s) => {
        const client = s === homeSchema ? supabase : supabase.schema(s);
        const visitsP = (async () => {
          // Started BEFORE the Supabase read so the two round trips overlap.
          const rdsStarted = cutoverVisits
            ? startSearchVisitsFromRds(s, { searchValues, startDate, endDate })
            : null;
          try {
            const { data, error } = await buildQuery(client);
            if (error) {
              console.warn(`[search] ${s}.${tableName}:`, error.message);
              return [];
            }
            let rows = data || [];
            if (rdsStarted) {
              const served = await serveSearchVisitsFromRds(s, {
                searchValues, startDate, endDate, supabaseRows: rows, started: rdsStarted,
              });
              rows = served.rows;
            }
            return rows.map((r) => ({ ...r, _schema: s }));
          } catch (e) {
            console.warn(`[search] ${s}.${tableName} threw:`, e?.message);
            return [];
          }
        })();
        const rosterP = (async () => {
          if (!includeRoster) return [];
          const rdsStarted = cutoverRoster
            ? startSearchRosterFromRds(s, { filters: rosterPlan.filters })
            : null;
          try {
            const { data, error } = await buildRosterQuery(client);
            if (error) {
              console.warn(`[search] ${s}.patients_with_ehrid:`, error.message);
              return [];
            }
            let rows = data || [];
            if (rdsStarted) {
              const served = await serveSearchRosterFromRds(s, {
                filters: rosterPlan.filters, supabaseRows: rows, started: rdsStarted,
              });
              rows = served.rows;
            }
            // Same translation the patient-level chart uses, so a roster row
            // arrives in the notes-c shape ResultsTable renders (`EHR_ID`,
            // `tel_num`) — and registers its id, which is what makes the chart
            // open against patients_with_ehrid rather than notes-c.
            return rows.map((r) => mapRosterRowToPatient(r, s));
          } catch (e) {
            console.warn(`[search] ${s}.patients_with_ehrid threw:`, e?.message);
            return [];
          }
        })();
        const [visits, roster] = await Promise.all([visitsP, rosterP]);
        return { visits, roster };
      })
    );

    // Every matching row from BOTH tables. This deliberately does NOT hide a
    // patient's own record just because they also have a visit: the two rows
    // are different things (a visit vs. the patient) and open different pages,
    // and suppressing one made a patient who was plainly in
    // patients_with_ehrid look absent from the search.
    const merged = sortSearchResults([
      ...perSchema.flatMap((r) => r.visits),
      ...perSchema.flatMap((r) => r.roster),
    ]);

    const hydrated = await hydrateScheduleRowsTimezoneSource(supabase, merged);
    setResults(hydrated);
    // Two different reasons the patient records may be missing, and they need
    // different wording: one is fixed by clearing a field, the other isn't.
    const roleAllowsRoster = hasClinicalAdminPermissions(role);
    setRosterSkipped(!rosterPlan.include && roleAllowsRoster ? rosterPlan.unsupported : []);
    setRosterRoleBlocked(!roleAllowsRoster);
  };

  // Pressing Enter in any search field runs the search, same as clicking Search.
  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const openMarkdownInNewTab = (fileData) => {
    const newWindow = window.open("", "_blank");
    newWindow.document.write(`
    <!DOCTYPE html>
    <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Formatted JSON Viewer</title>
      </head>
      <body>
        <div id="content">${fileData}</div>
      </body>
    </html>
  `);
    newWindow.document.close();
  };

  const handleViewJSONFiles = (jsonContent) => {
    try {
      const jsonData = JSON.parse(jsonContent);
      const formattedText = jsonToFormattedText(jsonData);
      openMarkdownInNewTab(formattedText);
    } catch (error) {
      console.error("Error parsing JSON content:", error);
      alert("Error parsing JSON content");
    }
  };

  const handleViewReports = async (folderPath) => {
    const supabase = await getSupabase();
    setCurrentFolderPath(folderPath);
    if (!folderPath) {
      setCurrentFolderFiles([]);
      setModalIsOpen(true);
      return;
    }

    const { data, error } = await supabase.storage
      .from(bucketName_files)
      .list(folderPath);

    if (error) {
      console.error("Error fetching files from Supabase:", error);
    } else {
      setCurrentFolderFiles(data);
      setModalIsOpen(true);
    }
  };

  // Standalone /search selects many rows (for Delete Rows); embedded as a
  // patient picker (onPatientSelect) it stays single-select, since the picker
  // wants exactly one patient. `selectedPatient` — what Edit / Upload act on —
  // is the row ticked most recently, or the last one still ticked.
  const handleCheckboxChange = (id) => {
    const multi = !onPatientSelect;
    setSelectedRows((prevSelectedRows) => {
      if (prevSelectedRows[id]) {
        const { [id]: removed, ...remainingSelectedRows } = prevSelectedRows;
        const remainingIds = Object.keys(remainingSelectedRows);
        const fallback = multi && remainingIds.length
          ? results.find((patient) => patient.id === remainingIds[remainingIds.length - 1]) || {}
          : {};
        setSelectedPatient(fallback);
        // Call callback if provided
        if (onPatientSelect) {
          onPatientSelect(null);
        }
        return remainingSelectedRows;
      } else {
        const selectedPatient = results.find((patient) => patient.id === id);
        setSelectedPatient(selectedPatient);
        // Call callback if provided
        if (onPatientSelect && selectedPatient) {
          onPatientSelect(selectedPatient);
        }
        return multi ? { ...prevSelectedRows, [id]: true } : { [id]: true };
      }
    });
  };

  // Delete Rows: visit rows only, and only the ones this viewer may delete.
  // A patient record (roster row) lives in patients_with_ehrid and is not a
  // visit — it is removed from the patient chart, not from here. The
  // permission rule is the schedules table's: tenant-schema roles need
  // ownership or an admin/owner share; everyone else may delete what they
  // can see.
  const canDeleteRow = (row) => {
    if (!row || row._source === "roster") return false;
    if (!isInGroup(viewer.role, "tenantSchema") || !viewer.userId) return true;
    return resolveTenantRowAccess({
      viewerId: viewer.userId,
      rowUserId: row.user_id,
      sharedUsers: row.shared_users,
      shareAllOwnerIds: viewer.ownerIds,
      shareAllOwnerRoles: viewer.ownerRoles,
    }).canDeleteRow;
  };

  const selectedResultRows = results.filter((r) => r && selectedRows[r.id]);
  const deletableRows = selectedResultRows.filter(canDeleteRow);
  const skippedRosterCount = selectedResultRows.filter((r) => r._source === "roster").length;
  const skippedPermissionCount =
    selectedResultRows.length - deletableRows.length - skippedRosterCount;

  const handleDeleteRows = async () => {
    if (deletableRows.length === 0) return;
    setIsDeleting(true);
    try {
      const supabase = await getSupabase();
      const { deletedIds, failed } = await removeVisitRows({
        rows: deletableRows,
        clientFor: (schema) => (schema === homeSchema ? supabase : supabase.schema(schema)),
        homeSchema,
      });
      if (deletedIds.length > 0) {
        const gone = new Set(deletedIds.map(String));
        setResults((prev) => prev.filter((r) => !gone.has(String(r.id))));
        setSelectedRows((prev) => {
          const next = { ...prev };
          deletedIds.forEach((id) => delete next[id]);
          return next;
        });
        if (gone.has(String(selectedPatient?.id))) setSelectedPatient({});
      }
      const skipped = [];
      if (skippedRosterCount) skipped.push(`${skippedRosterCount} patient record${skippedRosterCount === 1 ? "" : "s"} skipped (delete from the chart)`);
      if (skippedPermissionCount) skipped.push(`${skippedPermissionCount} skipped (no permission)`);
      if (failed.length > 0) {
        toast({
          title: deletedIds.length ? `Removed ${deletedIds.length}, ${failed.length} failed` : "Failed to remove rows",
          description: `${failed[0].error}${skipped.length ? `. ${skipped.join("; ")}.` : ""}`,
          status: "error",
          duration: 7000,
          isClosable: true,
        });
      } else {
        toast({
          title: deletedIds.length === 1
            ? "Row and its documents removed"
            : `${deletedIds.length} rows and their documents removed`,
          description: skipped.length ? `${skipped.join("; ")}.` : undefined,
          status: skipped.length ? "warning" : "success",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("[search] delete rows failed:", error);
      toast({
        title: "Failed to remove rows",
        description: error?.message || "An unexpected error occurred.",
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setIsDeleting(false);
      setIsDeleteConfirmOpen(false);
    }
  };

  // For the count line. A visit and a patient record are different things
  // (see the "patient record" chip in the table), so they are counted apart.
  const rosterCount = results.filter((r) => r._source === "roster").length;
  const visitCount = results.length - rosterCount;
  const entityLabel = getLabel(userRole, "entity").toLowerCase();

  return (
    <Box
      py={4}
      px={{ base: 2, md: 8, lg: 12 }}
      borderWidth="1px"
      borderRadius="lg"
      overflowX="auto"
    >
      {/* Cross-schema providers picker — search spans every selected schema.
          Wrapped in #clinic-root because the dropdown is Tailwind-styled and
          Tailwind utilities are scoped to that id (this page is Chakra). */}
      <div id="clinic-root" style={{ marginBottom: 12, display: "flex", justifyContent: "flex-end" }}>
        <ProvidersDropdown userRole={userRole} />
      </div>

      {/* Mobile-friendly search layout */}
      <Box display={{ base: "block", md: "none" }} onKeyDown={handleSearchKeyDown}>
        <VStack spacing={3} align="stretch">
          {searchPrompts
            .filter(
              ([columnName]) => columnName !== "date_" && columnName !== "time_"
            )
            .map(([columnName, label]) => (
              <Box key={columnName}>
                <Text fontSize="sm" fontWeight="medium" mb={1}>
                  {label}
                </Text>
                <InputGroup size="sm">
                  <Input
                    placeholder={
                      DATE_SEARCH_COLUMNS.includes(columnName)
                        ? `Enter ${label} (any format, e.g. 9/1/2026)`
                        : `Enter ${label}`
                    }
                    value={searchValues[columnName] || ""}
                    onChange={(e) =>
                      setSearchValues((prev) => ({
                        ...prev,
                        [columnName]: e.target.value,
                      }))
                    }
                    fontSize="sm"
                  />
                  {searchValues[columnName] && (
                    <InputRightElement>
                      <IconButton
                        size="xs"
                        icon={<FiX />}
                        onClick={() =>
                          setSearchValues((prev) => ({
                            ...prev,
                            [columnName]: "",
                          }))
                        }
                        aria-label="Clear input"
                      />
                    </InputRightElement>
                  )}
                </InputGroup>
              </Box>
            ))}
          {/* Date inputs for mobile */}
          <Box>
            <Text fontSize="sm" fontWeight="medium" mb={1}>
              Date Range
            </Text>
            <HStack spacing={2}>
              <Box flex={1}>
                <Text fontSize="xs" mb={1}>
                  Start Date
                </Text>
                <InputGroup size="sm">
                  <Input
                    type="date"
                    placeholder="Start Date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    fontSize="sm"
                  />
                  {startDate && (
                    <InputRightElement>
                      <IconButton
                        size="xs"
                        icon={<FiX />}
                        onClick={() => setStartDate("")}
                        aria-label="Clear start date"
                      />
                    </InputRightElement>
                  )}
                </InputGroup>
              </Box>
              <Box flex={1}>
                <Text fontSize="xs" mb={1}>
                  End Date
                </Text>
                <InputGroup size="sm">
                  <Input
                    type="date"
                    placeholder="End Date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    fontSize="sm"
                  />
                  {endDate && (
                    <InputRightElement>
                      <IconButton
                        size="xs"
                        icon={<FiX />}
                        onClick={() => setEndDate("")}
                        aria-label="Clear end date"
                      />
                    </InputRightElement>
                  )}
                </InputGroup>
              </Box>
            </HStack>
          </Box>
        </VStack>
      </Box>

      {/* Desktop table layout */}
      <Box display={{ base: "none", md: "block" }} overflowX="auto" onKeyDown={handleSearchKeyDown}>
        <Table variant="simple" size="md">
          {/* Table Header */}
          <Thead>
            <Tr>
              {searchPrompts.map((d) => (
                <Th key={d[0]} minW="150px" px={2}>
                  {d[1]}
                </Th>
              ))}
              <Th minW="150px" px={2}>
                Start Date
              </Th>
              <Th minW="150px" px={2}>
                End Date
              </Th>
            </Tr>
          </Thead>
          {/* Table Inputs */}
          <Tbody>
            <Tr>
              {searchPrompts
                .filter(
                  ([columnName]) =>
                    columnName !== "date_" && columnName !== "time_"
                )
                .map(([columnName, label]) => (
                  <Td key={columnName} px={2}>
                    <InputGroup size="md">
                      <Input
                        placeholder={`${label}`}
                        value={searchValues[columnName] || ""}
                        onChange={(e) =>
                          setSearchValues((prev) => ({
                            ...prev,
                            [columnName]: e.target.value,
                          }))
                        }
                        fontSize="sm"
                      />
                      {searchValues[columnName] && (
                        <InputRightElement>
                          <IconButton
                            size="sm"
                            icon={<FiX />}
                            onClick={() =>
                              setSearchValues((prev) => ({
                                ...prev,
                                [columnName]: "",
                              }))
                            }
                            aria-label="Clear input"
                          />
                        </InputRightElement>
                      )}
                    </InputGroup>
                  </Td>
                ))}
              {/* Start Date Input with Clear Button */}
              <Td px={2}>
                <InputGroup size="md">
                  <Input
                    type="date"
                    placeholder="Start Date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    fontSize="sm"
                  />
                  <InputRightElement>
                    <IconButton
                      size="sm"
                      icon={<FiX />}
                      onClick={() => setStartDate("")}
                      aria-label="Clear start date"
                    />
                  </InputRightElement>
                </InputGroup>
              </Td>
              {/* End Date Input with Clear Button */}
              <Td px={2}>
                <InputGroup size="md">
                  <Input
                    type="date"
                    placeholder="End Date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    fontSize="sm"
                  />
                  <InputRightElement>
                    <IconButton
                      size="sm"
                      icon={<FiX />}
                      onClick={() => setEndDate("")}
                      aria-label="Clear end date"
                    />
                  </InputRightElement>
                </InputGroup>
              </Td>
            </Tr>
          </Tbody>
        </Table>
      </Box>
      <HStack
        mt={4}
        justify="center"
        spacing={{ base: 2, md: 4 }}
        flexWrap="wrap"
      >
        <Button
          px={{ base: 4, md: 6 }}
          size={{ base: "sm", md: "md" }}
          colorScheme="teal"
          onClick={handleSearch}
          isLoading={isSearching}
          loadingText="Searching"
        >
          Search
        </Button>
        <Button
          px={{ base: 4, md: 6 }}
          size={{ base: "sm", md: "md" }}
          colorScheme="gray"
          onClick={handleClearAll}
          isDisabled={isSearching}
        >
          Clear All
        </Button>
      </HStack>

      {/* Search is a two-table fan-out across every selected provider, so it can
          take a moment. The button's own spinner is easy to miss when the eye is
          on the results area — which, before this, simply stayed blank. */}
      {isSearching && (
        <VStack py={10} spacing={3} data-testid="search-loading">
          <Spinner size="lg" thickness="3px" color="teal.500" emptyColor="gray.200" />
          <Text fontSize="sm" color="gray.600">
            Searching {getLabel(userRole, "entity").toLowerCase()}s…
          </Text>
        </VStack>
      )}

      {/* A search that found nothing has to SAY so. Rendering the same blank
          page as a fresh load made a zero-result search look like the search
          never ran. */}
      {!isSearching && hasSearched && results.length === 0 && (
        <VStack py={10} spacing={1}>
          <Text fontSize="sm" fontWeight="medium">No results</Text>
          <Text fontSize="xs" color="gray.600">
            Nothing matched in the visit or patient records for the selected providers.
          </Text>
        </VStack>
      )}

      {/* Same reasoning as the note below, but a different remedy: no amount of
          changing the search brings these rows back. */}
      {!isSearching && hasSearched && rosterRoleBlocked && (
        <Box
          mt={4}
          px={3}
          py={2}
          borderWidth="1px"
          borderRadius="md"
          borderColor="gray.300"
          bg="gray.50"
        >
          <Text fontSize="xs" color="gray.700" data-testid="roster-role-blocked">
            Searched visits only — patient records aren’t available for your role.
          </Text>
        </Box>
      )}

      {/* The patient records were left out of this search. Said out loud,
          because otherwise a never-scheduled patient looks like they don't
          exist rather than like they weren't looked for. */}
      {!isSearching && rosterSkipped.length > 0 && (
        <Box
          mt={4}
          px={3}
          py={2}
          borderWidth="1px"
          borderRadius="md"
          borderColor="orange.300"
          bg="orange.50"
        >
          <Text fontSize="xs" color="orange.800">
            Searched visits only. Patient records have no{" "}
            {rosterSkipped.map((c) => rosterFieldLabel(c)).join(" or ")}, so
            patients without a visit weren’t included. Clear{" "}
            {rosterSkipped.length === 1 ? "that field" : "those fields"} to search them too.
          </Text>
        </Box>
      )}

      {results.length > 0 && (
        <>
          {/* Said in numbers. The table below has no pagination, so this is
              every row it shows. The breakdown appears only when patient
              records are among them. */}
          <Text fontSize="sm" color="gray.700" mt={4} mb={2} data-testid="search-result-count">
            <Text as="span" fontWeight="semibold">
              {results.length} {results.length === 1 ? "result" : "results"}
            </Text>
            {rosterCount > 0 && (
              <Text as="span" color="gray.600">
                {" · "}
                {visitCount} {visitCount === 1 ? "visit" : "visits"}
                {" · "}
                {rosterCount} {entityLabel} {rosterCount === 1 ? "record" : "records"}
              </Text>
            )}
          </Text>
          <HStack spacing={{ base: 2, md: 4 }} mb={4} flexWrap="wrap">
            {/* Uploader writes the uploaded file back onto the notes-c row, so
                a roster selection would upload and then update zero rows.
                Both it and Edit are visit-only; the patient chart is where a
                patient-level row is worked on. */}
            {!disableUploader && selectedPatient._source !== "roster" && (
              <Uploader selection={selectedPatient} table={results} />
            )}
            {selectedPatient._source === "roster" && (
              <Text fontSize="xs" color="gray.600" data-testid="roster-selection-hint">
                Open this {getLabel(userRole, "entity").toLowerCase()}’s chart to
                upload files or edit their details.
              </Text>
            )}
            {/* EditPatientModal always writes to notes-c, so a roster row's id
                matches nothing there and Save would update zero rows without
                complaining. Their details are edited on the patient chart. */}
            <Button
              size={{ base: "sm", md: "md" }}
              colorScheme="orange"
              isDisabled={
                selectedPatient.id === undefined || selectedPatient._source === "roster"
              }
              title={
                selectedPatient._source === "roster"
                  ? "Open this patient's chart to edit their details"
                  : undefined
              }
              onClick={() => setIsEditPatientModalOpen(true)}
            >
              Edit Patient
            </Button>
            {/* Standalone page only: the picker's single selection is not a
                deletion queue. Stays enabled for a mixed selection — the
                deletable rows go and the rest are named in the toast. */}
            {!onPatientSelect && (
              <Button
                size={{ base: "sm", md: "md" }}
                colorScheme="red"
                variant="outline"
                isDisabled={deletableRows.length === 0 || isDeleting}
                title={
                  selectedResultRows.length > 0 && deletableRows.length === 0
                    ? skippedRosterCount === selectedResultRows.length
                      ? "Patient records are removed from the patient chart, not from search"
                      : "You do not have permission to remove the selected rows"
                    : "Delete the selected visit rows and their documents"
                }
                onClick={() => setIsDeleteConfirmOpen(true)}
                data-testid="delete-rows-button"
              >
                Delete Rows ({deletableRows.length})
              </Button>
            )}
          </HStack>
          <AlertDialog
            isOpen={isDeleteConfirmOpen}
            leastDestructiveRef={deleteCancelRef}
            onClose={() => !isDeleting && setIsDeleteConfirmOpen(false)}
            isCentered
          >
            <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader fontSize="lg" fontWeight="bold">
                  Delete {deletableRows.length === 1 ? "this row" : `${deletableRows.length} rows`}?
                </AlertDialogHeader>
                <AlertDialogBody>
                  <VStack align="stretch" spacing={2}>
                    <Text>
                      {deletableRows.length === 1
                        ? "This visit row and its chart document will be permanently deleted."
                        : `These ${deletableRows.length} visit rows and their chart documents will be permanently deleted.`}{" "}
                      This cannot be undone.
                    </Text>
                    <Box as="ul" pl={5} maxHeight="160px" overflowY="auto" fontSize="sm" data-testid="delete-rows-list">
                      {deletableRows.map((r) => (
                        <Text as="li" key={`${r._schema || homeSchema}-${r.id}`}>
                          {r.patient_name || r.name || "Unknown"}
                          {r.date_ ? ` — ${r.date_}` : ""}
                          {r._schema && r._schema !== homeSchema ? ` (${r._schema})` : ""}
                        </Text>
                      ))}
                    </Box>
                    {(skippedRosterCount > 0 || skippedPermissionCount > 0) && (
                      <Text fontSize="sm" color="orange.600">
                        {skippedRosterCount > 0 &&
                          `${skippedRosterCount} selected patient record${skippedRosterCount === 1 ? " is" : "s are"} not a visit and will be skipped — remove those from the patient chart. `}
                        {skippedPermissionCount > 0 &&
                          `${skippedPermissionCount} selected row${skippedPermissionCount === 1 ? "" : "s"} will be skipped (no permission).`}
                      </Text>
                    )}
                  </VStack>
                </AlertDialogBody>
                <AlertDialogFooter>
                  <Button ref={deleteCancelRef} onClick={() => setIsDeleteConfirmOpen(false)} isDisabled={isDeleting}>
                    Cancel
                  </Button>
                  <Button colorScheme="red" onClick={handleDeleteRows} ml={3} isLoading={isDeleting} loadingText="Deleting...">
                    Delete
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialogOverlay>
          </AlertDialog>
          <EditPatientModal
            isOpen={isEditPatientModalOpen}
            onClose={() => setIsEditPatientModalOpen(false)}
            patientData={selectedPatient}
            userRole={userRole}
            onUpdatePatient={(updatedPatient) => {
              setResults((prev) =>
                prev.map((p) =>
                  p.id === updatedPatient.id ? updatedPatient : p
                )
              );

              // Set localStorage flag to notify PatientPage to refresh
              localStorage.setItem("patientUpdated", updatedPatient.id);
            }}
          />
          <ResultsTable
            // Standalone /search page: clicking a row opens that row's chart.
            // When embedded as a patient picker (onPatientSelect provided, e.g.
            // the reassign/assign modals), keep row-click = select instead.
            navigateOnRowClick={!onPatientSelect}
            results={results}
            setResults={setResults}
            prompts={searchPrompts}
            homeSchema={homeSchema}
            selectedRows={selectedRows} // No row selection needed here
            handleCheckboxChange={handleCheckboxChange}
            handleViewJSONFiles={handleViewJSONFiles}
            handleViewFolderFiles={handleViewReports}
            currentFolderPath={currentFolderPath}
            previousFolderPath={previousFolderPath}
            supabaseUrl={supabaseUrl}
            bucketName_files={bucketName_files}
            modalIsOpen={modalIsOpen}
            setModalIsOpen={setModalIsOpen}
            currentFolderFiles={currentFolderFiles}
            showCheckboxes={showCheckboxes}
            viewerTimezone={viewerTimezone}
            reportFoldersWithFiles={reportCheck.folders}
            reportFilesChecked={reportCheck.for === results}
          />
        </>
      )}
    </Box>
  );
};

export default SearchComponent;
